import React, { useEffect, useState } from 'react';
import {
  Button, Table, Badge, Spinner,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../Components/Error';
import BookingServiceInputModal from './Components/BookingServiceInputModal';
import useModalState from '../../Hooks/useModalState';
import BookingServicesService from '../../Services/BookingServicesService';

function formatPrice(amount, currency) {
  if (amount == null) return '—';
  if (currency === 'HUF') return `${Math.round(amount).toLocaleString()} Ft`;
  return `€${Number(amount).toFixed(2)}`;
}

function BookingServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const inputModal = useModalState();

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await BookingServicesService.fetchBookingServices();
      setServices(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load booking services');
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = _.debounce(fetchServices, 500);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, []);

  const handleDelete = async (service) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to delete "${service.name}"?`)) {
      return;
    }
    try {
      await BookingServicesService.deleteBookingService(service.Id);
      fetchServices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete booking service');
    }
  };

  return (
    <>
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Booking
              {' '}
              <span className="hero-highlight">Extras</span>
            </h1>
            <p className="lead text-muted mb-4">
              Manage optional add-on services customers can attach to a booking
              (flowers, champagne, welcome drinks, etc.).
            </p>
            <div className="hero-actions d-flex gap-2">
              <Button
                onClick={() => inputModal.show({})}
                size="lg"
                variant="primary"
              >
                <i className="fa fa-plus me-2" />
                New Service
              </Button>
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-gift fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading booking services...</p>
        </div>
      ) : (
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Price (EUR)</th>
              <th>Price (HUF)</th>
              <th>Type</th>
              <th>Status</th>
              <th style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {services.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  <i className="fa fa-gift fa-2x mb-2 d-block opacity-50" />
                  No booking services yet. Create your first one.
                </td>
              </tr>
            ) : (
              services.map((svc) => (
                <tr key={svc.Id}>
                  <td>
                    <strong>{svc.name}</strong>
                    {svc.description && (
                      <small className="text-muted d-block text-truncate" style={{ maxWidth: 250 }}>
                        {svc.description}
                      </small>
                    )}
                  </td>
                  <td>{svc.category || '—'}</td>
                  <td>{formatPrice(svc.price_eur, 'EUR')}</td>
                  <td>{formatPrice(svc.price_huf, 'HUF')}</td>
                  <td>
                    <Badge bg="light" text="dark">
                      {svc.price_type === 'per_person' ? 'per person' : 'flat'}
                    </Badge>
                  </td>
                  <td>
                    {svc.active === 1 ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => inputModal.show(svc)}
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(svc)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {inputModal.isVisible && (
        <BookingServiceInputModal
          originalService={inputModal.selectedObject}
          show={inputModal.isVisible}
          fetchServices={fetchServices}
          handleClose={inputModal.hide}
        />
      )}
    </>
  );
}

export default BookingServices;
